/**
 * Speech Coach typed API (Phase 7). Every shape — input body, path params,
 * resolved value — derives from the SERVER's route defs via the type-only
 * import below (erased at build; no server code reaches the bundle). A
 * server-side shape change breaks this file and its callers at compile time.
 *
 * GET /sessions declares no `query:` schema (pagination is hand-read in the
 * handler via `ctx.url`), so callers pass page/pageSize through typedRoute's
 * free-form `query` field.
 *
 * Hand-rolled entries (the multipart exception — typedRoute is JSON-only):
 * - uploadAudio (POST /sessions/upload) and sessionAudio (POST /sessions/audio)
 *   send multipart/form-data, which isn't a typedRoute shape. They take a
 *   FormData and go through apiFetch with a `headers: {}` override so the
 *   browser sets the multipart boundary itself (apiFetch's default JSON
 *   Content-Type would corrupt it); their outputs still derive from the defs.
 *   sessionAudio's success shape is `{ session, aiOutput, transcript }`; its
 *   error Responses (503/502/402) are excluded from the derived type — callers
 *   already handle those via thrown apiFetch errors.
 */
import { apiFetch, typedRoute } from "@d12labs/client";
import type { speechCoachRouteDefs } from "../server/routes";

/** A def's resolved value — the handler's return type minus raw Response branches. */
type OutputOf<D extends { handler: (ctx: never) => unknown }> = Exclude<
	Awaited<ReturnType<D["handler"]>>,
	Response
>;

export const speechCoachApi = {
	summary: typedRoute<typeof speechCoachRouteDefs.summary>()(
		"GET",
		"/api/plugins/speech-coach/summary",
	),

	achievements: typedRoute<typeof speechCoachRouteDefs.achievements>()(
		"GET",
		"/api/plugins/speech-coach/achievements",
	),

	getPlan: typedRoute<typeof speechCoachRouteDefs.getPlan>()(
		"GET",
		"/api/plugins/speech-coach/plan",
	),
	patchPlan: typedRoute<typeof speechCoachRouteDefs.patchPlan>()(
		"PATCH",
		"/api/plugins/speech-coach/plan",
	),
	generatePlan: typedRoute<typeof speechCoachRouteDefs.generatePlan>()(
		"POST",
		"/api/plugins/speech-coach/plan/generate",
	),

	drills: typedRoute<typeof speechCoachRouteDefs.drills>()(
		"GET",
		"/api/plugins/speech-coach/drills",
	),

	createSession: typedRoute<typeof speechCoachRouteDefs.createSession>()(
		"POST",
		"/api/plugins/speech-coach/sessions",
	),
	listSessions: typedRoute<typeof speechCoachRouteDefs.listSessions>()(
		"GET",
		"/api/plugins/speech-coach/sessions",
	),

	// Hand-rolled (multipart isn't a typedRoute shape) — def-derived output.
	uploadAudio: (fd: FormData): Promise<OutputOf<typeof speechCoachRouteDefs.uploadAudio>> =>
		apiFetch<OutputOf<typeof speechCoachRouteDefs.uploadAudio>>(
			"/api/plugins/speech-coach/sessions/upload",
			// Empty headers override apiFetch's JSON Content-Type so the browser
			// sets the multipart boundary from the FormData body.
			{ method: "POST", body: fd, headers: {} },
		),

	// Hand-rolled (multipart isn't a typedRoute shape) — def-derived output.
	sessionAudio: (fd: FormData): Promise<OutputOf<typeof speechCoachRouteDefs.sessionAudio>> =>
		apiFetch<OutputOf<typeof speechCoachRouteDefs.sessionAudio>>(
			"/api/plugins/speech-coach/sessions/audio",
			// Empty headers override apiFetch's JSON Content-Type so the browser
			// sets the multipart boundary from the FormData body.
			{ method: "POST", body: fd, headers: {} },
		),

	scoreSession: typedRoute<typeof speechCoachRouteDefs.scoreSession>()(
		"POST",
		"/api/plugins/speech-coach/sessions/:id/score",
	),

	appendDrill: typedRoute<typeof speechCoachRouteDefs.appendDrill>()(
		"POST",
		"/api/plugins/speech-coach/plan/drills",
	),
};

/** The server's response shapes, derived — never redeclared by hand. */

/** The active plan row (`GET /plan`, non-null branch). */
export type SpeechCoachPlan = NonNullable<
	Awaited<ReturnType<typeof speechCoachApi.getPlan>>["plan"]
>;

/** A drill row (`GET /drills` — curated and assigned share the shape). */
export type SpeechCoachDrill = Awaited<ReturnType<typeof speechCoachApi.drills>>["curated"][number];

/** A practice-session row (item of the `GET /sessions` page). */
export type SpeechCoachSession = Awaited<
	ReturnType<typeof speechCoachApi.listSessions>
>["items"][number];

/** AI drill-score feedback payload (a session's `aiFeedback`, non-null once scored). */
export type DrillScoreFeedback = NonNullable<SpeechCoachSession["aiFeedback"]>;

/** Achievement catalog entry (`GET /achievements`). */
export type AchievementDefinition = Awaited<
	ReturnType<typeof speechCoachApi.achievements>
>["catalog"][number];
